import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'react-emotion';
import { withTheme } from 'emotion-theming';
import i18next from 'i18next';
import Range from 'rc-slider/lib/Range';

import objectsDeepNotEqualComparison from '~/modules/core/utils/jsHelpers/objectsDeepComparison';

import {
  LargeSpacer,
  XXLargeSpacer,
  MediumSpacer,
} from '~/modules/coreUI/components/legacies/Spacers';
import { BasicButton } from '~/modules/coreUI/components/legacies/Button';
import { SmallLabel } from '~/modules/coreUI/components/legacies/Labels';
import { Row, Column } from '~/modules/coreUI/components/layouts/helpers/LinearLayout';
import { currentCurrencyDisplayName } from '~/modules/ecommerceCore/utils/changeCurrency';

import RangeSliderContainer from './RangeSliderContainer';

const toZeroIfNaN = value => (isNaN(value) ? 0 : value); // eslint-disable-line

const PriceInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.paddings.medium}px;
  border: ${props => props.theme.borders.size.thin}px solid
    ${props => props.theme.borders.color.light};
  border-radius: ${props => props.theme.borders.radius.normal}px;
  ::-webkit-input-placeholder {
    color: ${props => props.theme.colors.labels.subtle};
  }
  font-size: ${props => props.theme.fonts.sizes.small}px;
  color: ${props => props.theme.colors.labels.subtle};
`;
const ValuesNames = {
  minPrice: 'min_price',
  maxPrice: 'max_price',
  lowestPrice: 'lowest_price',
  highestPrice: 'highest_price',
};

const extractPricesValues = (listOfOptions) => {
  const result = {};

  for (let i = 0; i < listOfOptions.length; i += 1) {
    const option = listOfOptions[i];
    result[option.name] = option.value;
  }

  return result;
};

class PriceFilterWrapper extends Component {
  static getDerivedStateFromProps(nextProps, currentState) {
    const expectedPricesValues = extractPricesValues(nextProps.listOfOptions);
    const newPropsReceived = objectsDeepNotEqualComparison(
      currentState.previousFiltersProps,
      expectedPricesValues,
    );

    if (newPropsReceived) {
      return {
        previousFiltersProps: expectedPricesValues,
        ...expectedPricesValues,
      };
    }
    return null;
  }

  state = {
    previousFiltersProps: [],
  };

  onApplyClick = () => {
    const UpdatedListOfOptions = this.getUpdatedListOfOptions();

    this.props.onApplyClick(UpdatedListOfOptions);
  };

  getUpdatedListOfOptions = () => {
    const currentOptions = this.state;
    const updatedOptions = this.props.listOfOptions;

    for (let i = 0; i < updatedOptions.length; i += 1) {
      const option = updatedOptions[i];
      option.value = String(currentOptions[option.name]);
    }

    return updatedOptions;
  };

  updatePriceValues = (valueOfChange) => {
    if (Array.isArray(valueOfChange)) {
      this.setState({
        [ValuesNames.minPrice]: valueOfChange[0],
        [ValuesNames.maxPrice]: valueOfChange[1],
      });
    } else {
      this.setState({
        [valueOfChange.target.name]: valueOfChange.target.value,
      });
    }
  };

  updatePriceValuesAfterChange = (valueOfChange) => {
    this.setState({
      [ValuesNames.minPrice]: valueOfChange[0],
      [ValuesNames.maxPrice]: valueOfChange[1],
    });
  };

  render() {
    const lowestPrice = toZeroIfNaN(Math.trunc(this.state[ValuesNames.lowestPrice]));
    const highestPrice = toZeroIfNaN(Math.trunc(this.state[ValuesNames.highestPrice]));
    const minPrice = toZeroIfNaN(Math.trunc(this.state[ValuesNames.minPrice]));
    const maxPrice = toZeroIfNaN(Math.trunc(this.state[ValuesNames.maxPrice]));

    const defaultValue = [minPrice, maxPrice];

    const { theme } = this.props;

    return (
      <React.Fragment ref={this.simulateClick}>
        <Row spaceBetweenJustified>
          <Column grow>
            <Row leftJustified fullWidth>
              <SmallLabel htmlFor={ValuesNames.minPrice}>
                {i18next.t('ecommerceCoreUI:productFilters.priceRangeFrom')}
              </SmallLabel>
            </Row>
            <MediumSpacer />
            <PriceInput
              type="number"
              placeholder={`${minPrice} ${currentCurrencyDisplayName()}`}
              name={ValuesNames.minPrice}
              value={minPrice}
              onChange={this.updatePriceValues}
            />
          </Column>
          <XXLargeSpacer />
          <Column grow>
            <Row leftJustified fullWidth>
              <SmallLabel htmlFor={ValuesNames.maxPrice}>
                {i18next.t('ecommerceCoreUI:productFilters.priceRangeTo')}
              </SmallLabel>
            </Row>
            <MediumSpacer />
            <PriceInput
              type="number"
              placeholder={`${maxPrice} ${currentCurrencyDisplayName()}`}
              name={ValuesNames.maxPrice}
              onChange={this.updatePriceValues}
              value={maxPrice}
            />
          </Column>
        </Row>
        <XXLargeSpacer />
        <RangeSliderContainer>
          <Range
            pushable
            count={3}
            min={lowestPrice}
            max={highestPrice}
            defaultValue={defaultValue}
            value={[minPrice, maxPrice]}
            onChange={this.updatePriceValues}
            onAfterChange={this.updatePriceValuesAfterChange}
            trackStyle={[
              {
                backgroundColor: theme.colors.secondary,
              },
            ]}
            handleStyle={[
              {
                backgroundColor: theme.colors.secondary,
                borderColor: theme.colors.named.white,
                boxShadow: '0 0 6px 0 rgba(0, 0, 1, 0.25)',
                width: 20,
                height: 20,
                marginTop: -8,
              },
              {
                backgroundColor: theme.colors.secondary,
                borderColor: theme.colors.named.white,
                boxShadow: '0 0 6px 0 rgba(0, 0, 1, 0.25)',
                width: 20,
                height: 20,
                marginTop: -8,
              },
            ]}
            activeDotStyle={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.named.white,
              boxShadow: theme.colors.secondaryClicked,
            }}
            railStyle={{
              height: 2,
              backgroundColor: '#dfdddd',
            }}
          />
        </RangeSliderContainer>
        <LargeSpacer />
        <Row rightJustified>
          <BasicButton
            primary
            inverted
            width="30%"
            padding="10"
            onClicked={() => this.onApplyClick()}
          >
            {i18next.t('ecommerceCoreUI:productFilters.apply')}
          </BasicButton>
        </Row>
        <LargeSpacer />
      </React.Fragment>
    );
  }
}

PriceFilterWrapper.propTypes = {
  listOfOptions: PropTypes.instanceOf(Array).isRequired,
  theme: PropTypes.shape({}).isRequired,
  onApplyClick: PropTypes.func.isRequired,
};

export default withTheme(PriceFilterWrapper);
